import {Component, Prop} from 'vue-property-decorator';
import {mapGetters} from 'vuex';
import {Client, Event, Order, PaymentMethod, Product, Slot} from '../Models';
import {CartDTO} from '../DTOs';
import {BaseMixin} from './BaseMixin';
import {mixins} from 'vue-class-component';
import {ClientService, EventService, OrderService, ProductService, SlotService, PaymentMethodService} from '../Services';
import {REDEEM_TYPE_DEFAULT, REDEEM_TYPE_GUEST_ONLY, REDEEM_TYPE_LOGIN_ONLY} from '../constants';

@Component({
  computed: {
    ...mapGetters({
      currentOrder: 'getCurrentOrder',
      cart: 'getCart',
      products: 'getProducts',
      events: 'getEvents',
      clients: 'getClients',
      slots: 'getSlots',
      paymentMethods: 'getPaymentMethods',
    }),
  },
})
export class VuexObjectMixin extends mixins(BaseMixin) {
  @Prop({type: String, required: false}) inventoryUrl: string | undefined;

  currentOrder!: Order | null;
  cart!: CartDTO | null;
  products!: Array<Product>;
  events!: Array<Event>;
  clients!: Array<Client>;
  slots!: Array<Slot>;
  paymentMethods!: Array<PaymentMethod>;

  protected productService!: ProductService;
  protected eventService!: EventService;
  protected slotService!: SlotService;
  protected orderService!: OrderService;
  protected clientService!: ClientService;
  protected paymentMethodService!: PaymentMethodService;

  created() {
    if (this.inventoryUrl) {
      this.productService = new ProductService(this.inventoryUrl);
      this.eventService = new EventService(this.inventoryUrl);
      this.orderService = new OrderService(this.inventoryUrl);
      this.clientService = new ClientService(this.inventoryUrl);
      this.slotService = new SlotService(this.inventoryUrl);
      this.paymentMethodService = new PaymentMethodService(this.inventoryUrl);
    }
  }

  get hasEvents(): boolean {
    return this.events && this.events.length > 0;
  }

  get mustOrderWithLogin(): boolean {
    if (!this.enableTicketShop || this.isRequesting) {
      return false;
    }

    let mustHaveLogin = false;
    for (const event of this.events) {
      let _allowedEventTypes = event.redeemTypes;
      const products = this.products.filter(p => p.eventId === event.id);
      const productsWithRedeemType = products.filter(p => p.redeemType !== null);
      const doesEventDisableGuestOrder = products.find(p => {
        const type = _allowedEventTypes.find(rt => rt.visitorSegmentId === p.visitorSegmentId);
        if (type) {
          return type.redeemType === REDEEM_TYPE_LOGIN_ONLY;
        }
        return false;
      }) !== undefined;

      // if a product has a redeemtype it overwrites the event setting
      if (productsWithRedeemType.length) {
        const anyProductAllowsGuestOrder = productsWithRedeemType.find(p =>
          p.redeemType !== null && [REDEEM_TYPE_DEFAULT, REDEEM_TYPE_GUEST_ONLY].includes(p.redeemType)) !== undefined;

        mustHaveLogin = !anyProductAllowsGuestOrder;
      } else if (doesEventDisableGuestOrder) {
        mustHaveLogin = true;
      }
    }

    return mustHaveLogin;
  }

  get mustOrderAsGuest(): boolean {
    if (!this.enableTicketShop || this.isRequesting) {
      return false;
    }

    let mustBeGuest = false;
    for (const event of this.events) {
      let _allowedEventTypes = event.redeemTypes;
      const products = this.products.filter(p => p.eventId === event.id);
      const productsWithRedeemType = products.filter(p => p.redeemType !== null);
      const doesEventDisableLogin = products.find(p => {
        const type = _allowedEventTypes.find(rt => rt.visitorSegmentId === p.visitorSegmentId);
        if (type) {
          return type.redeemType === REDEEM_TYPE_GUEST_ONLY;
        }
        return false;
      }) !== undefined;

      // if a product has a redeemtype it overwrites the event setting
      if (productsWithRedeemType.length) {
        const anyProductAllowsNonGuestOrder = productsWithRedeemType.find(p =>
          p.redeemType !== null && [REDEEM_TYPE_DEFAULT, REDEEM_TYPE_LOGIN_ONLY].includes(p.redeemType)) !== undefined;

        mustBeGuest = !anyProductAllowsNonGuestOrder;
      } else if (doesEventDisableLogin) {
        mustBeGuest = true;
      }
    }

    return mustBeGuest;
  }
}
